
import { supabase } from "@/integrations/supabase/client";
import { DetectedLocation } from '../mappings/LocationMapping';
import { TravelConstraints } from '../mappings/TravelDistanceMapping';
import { detectAccommodationType } from '../mappings/AccommodationMapping';
import { detectExperienceType } from '../mappings/ExperienceMapping';
import { applyLocationFilter } from '../filters/LocationFilter';
import { applyPriceFilter } from '../filters/PriceFilter';
import { applyFacilitiesFilter } from '../filters/FacilitiesFilter';
import { applyTravelDistanceFilter } from '../filters/TravelDistanceFilter';
import { applyAccommodationFilter } from '../filters/AccommodationFilter';
import { applyExperienceFilter } from '../filters/ExperienceFilter';
import { verifySearchWithSample } from '../utils/SearchVerification';
import { verifyAndFilterResults } from '../utils/ResultsVerification';

export const handleStandardSearch = async (
  searchTerms: string[],
  detectedLocation: DetectedLocation,
  priceConstraints: any,
  detectedFacilities: any,
  travelConstraints: TravelConstraints | null,
  query: string
) => {
  console.log('🔧 === STEP 5: BUILDING BASE QUERY ===');
  let dbQuery = supabase
    .from('Complete feed')
    .select('*')
    .not('imageURL', 'is', null)
    .not('name', 'is', null);

  console.log('✅ Base query initialized');

  // Apply location filter FIRST and STRICTLY
  console.log('🎯 === STEP 6: APPLYING STRICT LOCATION FILTER ===');
  if (detectedLocation) {
    console.log('Applying STRICT location filter for:', detectedLocation);
    dbQuery = applyLocationFilter(dbQuery, detectedLocation);
    console.log('✅ Strict location filter applied - no fallback to broad search');
  }
  
  // Apply price filters
  if (priceConstraints) {
    console.log('💰 === STEP 7: APPLYING PRICE FILTERS ===');
    console.log('Price constraints before applying:', priceConstraints);
    dbQuery = applyPriceFilter(dbQuery, priceConstraints);
    console.log('Price filter applied - query should now include price constraints');
  } else {
    console.log('💰 === STEP 7: NO PRICE FILTERS DETECTED ===');
  }

  // Apply facilities filters
  if (detectedFacilities) {
    console.log('🏨 === STEP 8: APPLYING FACILITIES FILTERS ===');
    dbQuery = applyFacilitiesFilter(dbQuery, detectedFacilities);
  } else {
    console.log('🏨 === STEP 8: NO FACILITIES FILTERS DETECTED ===');
  }

  // Apply travel constraints
  if (travelConstraints) {
    console.log('🛫 === STEP 9: APPLYING TRAVEL DISTANCE FILTERS ===');
    dbQuery = applyTravelDistanceFilter(dbQuery, travelConstraints);
  } else {
    console.log('🛫 === STEP 9: NO TRAVEL CONSTRAINTS DETECTED ===');
  }

  // Verify with sample
  await verifySearchWithSample(detectedLocation, priceConstraints, detectedFacilities);

  // Apply additional filters STRICTLY
  console.log('🔍 === STEP 11: DETECTING OTHER FILTERS ===');
  const detectedAccommodationType = detectAccommodationType(searchTerms);
  console.log('Accommodation detection result:', detectedAccommodationType);
  
  const detectedExperienceType = detectExperienceType(searchTerms);
  console.log('Experience detection result:', detectedExperienceType);

  console.log('🔧 === STEP 12: APPLYING STRICT ADDITIONAL FILTERS ===');
  if (detectedAccommodationType) {
    console.log('🏨 Applying STRICT accommodation filter...');
    dbQuery = applyAccommodationFilter(dbQuery, detectedAccommodationType);
  }

  if (detectedExperienceType) {
    if (detectedLocation?.type === 'city') {
      console.log('🚫 Skipping experience filter for city search to maintain strict location focus');
    } else {
      console.log('🏖️ Applying experience filter...');
      dbQuery = applyExperienceFilter(dbQuery, detectedExperienceType);
    }
  }

  // Execute query
  console.log('⚡ === STEP 13: EXECUTING STRICT QUERY ===');
  console.log('Final query about to be executed with STRICT filters applied');
  
  if (priceConstraints) {
    console.log('🔍 Final check - price constraints in final query:', priceConstraints);
  }
  
  const { data, error } = await dbQuery.limit(100);
  
  if (error) {
    console.error('❌ Database error:', error);
    throw error;
  }
  
  console.log(`📊 Strict query executed successfully - found ${data?.length || 0} results`);
  
  // Log sample of results for debugging
  if (data && data.length > 0) {
    console.log('🔍 Sample results for debugging:');
    data.slice(0, 3).forEach((item, index) => {
      console.log(`${index + 1}. ${item.name} - ${item.country}, ${item.city} - €${item.price || 'N/A'}`);
    });
  } else {
    console.log('❌ No results found with strict filtering - this is expected behavior for specific searches');
  }
  
  return verifyAndFilterResults(data || [], detectedLocation, priceConstraints, detectedFacilities, query);
};
